import { ReactNode } from "react";
import { useQuery } from "@apollo/client";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { CardContainer } from "components/shared/CardContainer/CardContainer";
import { DealerManagementTable } from "../../tables/dealerManagementTable/DealerManagementTable";
import { ModalsKeys } from "global";
import { useModal } from "hooks/useModal";
import { AddDealerDialog } from "../../dialogs/AddDealerDialog";
import { GET_PARTNER_LINKS } from "modules/aftermarketPartner/api";
import { PartnerLinksResponse } from "modules/aftermarketPartner/api/types";
import { aftermarketPartnerActions, aftermarketPartnerSelectors } from "modules/aftermarketPartner/model";
import { format, subDays } from "date-fns";
import { ModalsContainer } from "components/shared/Modals";
import { DealerManagementPartnerLink } from "modules/aftermarketPartner/types";
import { IntegrationSettingsDialog } from "modules/integrationSettings";

export const AftermarketPartnerDealerManagement = () => {
  const NTP_DYNAMICS_ACCOUNT_ID = "c04bb30d-b9e7-e011-9518-005056a20000";

  const dispatch = useDispatch();

  const account = useSelector((state: any) => state.account);
  const partnerLinks = useSelector(aftermarketPartnerSelectors.partnerLinks);

  const modalsWithKeys: Partial<Record<ModalsKeys, ReactNode>> = {
    [ModalsKeys.AftermarketPartnerAddDealerDialog]: <AddDealerDialog />,
    [ModalsKeys.IntegrationSettingsDialog]: <IntegrationSettingsDialog />,
  };

  const { handleOpen: handleAddDealerDialogOpen } = useModal(ModalsKeys.AftermarketPartnerAddDealerDialog);
  const { handleOpen: openIntegrationSettingsDialog } = useModal(ModalsKeys.IntegrationSettingsDialog);

  const isNTPAccount = account.dynamicsAccountId === NTP_DYNAMICS_ACCOUNT_ID;

  const { loading: loadingPartnerLinks, refetch: refetchPartnerLinks } = useQuery<PartnerLinksResponse>(
    GET_PARTNER_LINKS,
    {
      variables: {
        dynamicsId: account.dynamicsAccountId,
        metricsDateRange: {
          from: format(subDays(new Date(), 90), "yyyy-MM-dd"),
          to: format(new Date(), "yyyy-MM-dd"),
        },
      },
      onCompleted(data) {
        dispatch(aftermarketPartnerActions.setPartnerLinks(data.getPartnerLinks));
        dispatch(aftermarketPartnerActions.setIsNTPAccount(isNTPAccount));
      },
    }
  );

  const handleOpenIntegrationSettingsDialog = ({ partnerLink }: { partnerLink: DealerManagementPartnerLink }) => {
    openIntegrationSettingsDialog({
      integrationName: "NTP",
      dealerAccountId: partnerLink.accountId,
      dealerLocations: partnerLink.partnerLocations,
      dealerContacts: [],
    });
  };

  return (
    <>
      <CardContainer
        title="Dealer Management"
        actionBtn={
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleAddDealerDialogOpen({ refetchPartnerLinks })}>
            Add Dealer
          </Button>
        }>
        <DealerManagementTable
          loading={loadingPartnerLinks}
          tableData={partnerLinks}
          handleOpenIntegrationSettingsDialog={handleOpenIntegrationSettingsDialog}
        />
      </CardContainer>

      <ModalsContainer modalsWithKeys={modalsWithKeys} />
    </>
  );
};
